"use client";

import { motion, AnimatePresence, useInView } from "framer-motion";
import { EASE_OUT, springSnappy } from "./motion-tokens";
import { useCallback, useEffect, useRef, useState } from "react";
import { Minus, Play, Plus, Square } from "lucide-react";
import { RangeSlider } from "@web/src/components/ui/range-slider";

export function MetronomeDemo() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [timeSig] = useState(4);
  const [userTouched, setUserTouched] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nextBeatTimeRef = useRef(0);
  const beatCountRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.5 });

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    if (audioCtxRef.current.state === "suspended") {
      void audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playClick = useCallback(
    (isAccent: boolean) => {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = isAccent ? 1000 : 700;
      osc.type = "sine";

      gain.gain.setValueAtTime(isAccent ? 0.6 : 0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    },
    [getAudioContext],
  );

  const start = useCallback(() => {
    setIsPlaying(true);
    beatCountRef.current = 0;
    setCurrentBeat(0);

    const ctx = getAudioContext();
    nextBeatTimeRef.current = ctx.currentTime;

    const interval = setInterval(() => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;

      const now = ctx.currentTime;
      const beatDuration = 60 / bpm;

      while (nextBeatTimeRef.current < now + 0.1) {
        const beat = beatCountRef.current % timeSig;
        playClick(beat === 0);
        setCurrentBeat(beat);
        beatCountRef.current++;
        nextBeatTimeRef.current += beatDuration;
      }
    }, 25);

    intervalRef.current = interval;
  }, [bpm, timeSig, getAudioContext, playClick]);

  const stop = useCallback(() => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCurrentBeat(0);
    beatCountRef.current = 0;
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioCtxRef.current) void audioCtxRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    stop();
    start();
  }, [bpm]);

  return (
    <div ref={containerRef} className="w-full max-w-sm mx-auto">
      <div className="rounded-xl border border-border-primary bg-surface-secondary overflow-hidden shadow-2xl p-5">
        {/* BPM display with scroll animation */}
        <div className="text-center mb-4">
          <div className="relative h-14 overflow-hidden flex items-center justify-center">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={bpm}
                initial={{ y: 20, opacity: 0, filter: "blur(4px)" }}
                animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                exit={{ y: -20, opacity: 0, filter: "blur(4px)" }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
                className="text-5xl font-mono font-bold text-glyphs-primary tabular-nums"
              >
                {bpm}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="text-xs text-glyphs-secondary uppercase tracking-wider">
            BPM
          </div>
        </div>

        {/* Beat indicator bars (matching real metronome fullscreen style) */}
        <div className="flex items-end justify-center gap-4 py-3 mb-4">
          {Array.from({ length: timeSig }).map((_, i) => {
            const isAccent = i === 0;
            const isActive = isPlaying && currentBeat === i;
            return (
              <motion.div
                key={i}
                className="rounded-full"
                style={{
                  width: "clamp(0.75rem, 3vmin, 1.5rem)",
                }}
                animate={{
                  height: isAccent ? 64 : 44,
                  backgroundColor: isActive
                    ? "var(--color-theme-green)"
                    : "var(--color-border-primary)",
                  scale: isActive ? 1.15 : 1,
                }}
                transition={{
                  backgroundColor: { duration: 0.075 },
                  scale: {
                    type: "spring",
                    stiffness: isActive ? 700 : 400,
                    damping: isActive ? 18 : 30,
                  },
                }}
              />
            );
          })}
        </div>

        {/* BPM controls */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <motion.button
            className="w-10 h-10 rounded-full border border-border-primary bg-surface-tertiary flex items-center justify-center text-glyphs-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.93 }}
            transition={springSnappy}
            onClick={() => {
              setUserTouched(true);
              setBpm((b) => Math.max(30, b - 5));
            }}
          >
            <Minus className="w-4 h-4" />
          </motion.button>
          <RangeSlider
            min={30}
            max={240}
            value={bpm}
            onValueChange={(next) => {
              setUserTouched(true);
              setBpm(next);
            }}
            className="flex-1 w-auto [--range-accent:var(--color-theme-blue)]"
          />
          <motion.button
            className="w-10 h-10 rounded-full border border-border-primary bg-surface-tertiary flex items-center justify-center text-glyphs-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.93 }}
            transition={springSnappy}
            onClick={() => {
              setUserTouched(true);
              setBpm((b) => Math.min(240, b + 5));
            }}
          >
            <Plus className="w-4 h-4" />
          </motion.button>
        </div>

        {/* Play/Stop */}
        <motion.button
          className="w-full py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
          style={{
            backgroundColor: isPlaying ? "#FF6B6B" : "#007AFF",
            color: "white",
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={springSnappy}
          onClick={() => {
            setUserTouched(true);
            if (isPlaying) stop();
            else start();
          }}
        >
          {isPlaying ? (
            <>
              <Square className="w-4 h-4" fill="currentColor" />
              Stop
            </>
          ) : (
            <>
              <Play className="w-4 h-4" fill="currentColor" />
              Play
            </>
          )}
        </motion.button>

        {/* Song BPM list */}
        <div className="mt-4 pt-3 border-t border-border-primary">
          <div className="text-[10px] uppercase tracking-wider text-glyphs-secondary mb-1.5">
            Setlist BPMs
          </div>
          {[
            { name: "Amazing Grace", bpm: 72 },
            { name: "Way Maker", bpm: 132 },
            { name: "How Great Is Our God", bpm: 80 },
          ].map((song) => (
            <motion.button
              key={song.name}
              className="flex items-center justify-between w-full px-2 py-1.5 rounded text-left hover:bg-surface-tertiary transition-colors"
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setUserTouched(true);
                setBpm(song.bpm);
              }}
            >
              <span className="text-xs text-glyphs-primary truncate">
                {song.name}
              </span>
              <span className="text-[10px] font-mono text-glyphs-secondary">
                {song.bpm}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
